import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Home, Mail, Lock, User, Phone, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const roles = [
  { key: "customer", label: "Pencari Hunian" },
  { key: "owner", label: "Pemilik Properti" },
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "customer" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password,
        role: form.role,
      };
      if (form.phone.trim()) payload.phone = form.phone.trim();
      const user = await register(payload);
      toast.success("Akun berhasil dibuat!");
      navigate(`/${user.role}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="register-page">
      <div className="text-center mb-8">
        <span className="inline-grid place-items-center w-12 h-12 rounded-2xl bg-primary text-primary-foreground mb-4">
          <Home size={22} />
        </span>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Buat Akun AVASA</h1>
        <p className="mt-2 text-sm text-muted-foreground">Gratis, hanya butuh beberapa detik.</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-card border border-border rounded-2xl p-6 space-y-4">
        {error && (
          <div className="rounded-xl bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive" data-testid="register-error">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2" data-testid="register-role-toggle">
          {roles.map((r) => (
            <button
              type="button"
              key={r.key}
              data-testid={`register-role-${r.key}`}
              onClick={() => setForm({ ...form, role: r.key })}
              className={`py-2.5 rounded-xl text-sm font-semibold border transition-colors ${
                form.role === r.key
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-secondary/40 text-muted-foreground border-border"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        <div>
          <label className="text-sm font-medium">Nama Lengkap</label>
          <div className="mt-1.5 flex items-center gap-2 rounded-xl bg-secondary/50 px-3 focus-within:ring-2 focus-within:ring-primary focus-within:bg-background transition-colors">
            <User size={16} className="text-muted-foreground" />
            <input required data-testid="register-name-input" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Nama kamu" className="flex-1 bg-transparent outline-none text-sm py-3" />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium">Email</label>
          <div className="mt-1.5 flex items-center gap-2 rounded-xl bg-secondary/50 px-3 focus-within:ring-2 focus-within:ring-primary focus-within:bg-background transition-colors">
            <Mail size={16} className="text-muted-foreground" />
            <input type="email" required data-testid="register-email-input" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="nama@email.com" className="flex-1 bg-transparent outline-none text-sm py-3" />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium">No. Telepon <span className="text-muted-foreground font-normal">(opsional)</span></label>
          <div className="mt-1.5 flex items-center gap-2 rounded-xl bg-secondary/50 px-3 focus-within:ring-2 focus-within:ring-primary focus-within:bg-background transition-colors">
            <Phone size={16} className="text-muted-foreground" />
            <input data-testid="register-phone-input" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="08xxxxxxxxxx" className="flex-1 bg-transparent outline-none text-sm py-3" />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium">Kata Sandi</label>
          <div className="mt-1.5 flex items-center gap-2 rounded-xl bg-secondary/50 px-3 focus-within:ring-2 focus-within:ring-primary focus-within:bg-background transition-colors">
            <Lock size={16} className="text-muted-foreground" />
            <input type="password" required data-testid="register-password-input" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Min. 8 karakter, huruf & angka" className="flex-1 bg-transparent outline-none text-sm py-3" />
          </div>
        </div>

        <button type="submit" disabled={loading} data-testid="register-submit-button"
          className="w-full py-3 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60 disabled:hover:scale-100 flex items-center justify-center gap-2">
          {loading && <Loader2 size={16} className="animate-spin" />}
          {loading ? "Memproses..." : "Daftar"}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Sudah punya akun?{" "}
        <Link to="/login" className="font-semibold text-primary hover:underline" data-testid="register-to-login-link">
          Masuk
        </Link>
      </p>
    </div>
  );
}
